import React from 'react';
import { FileText, Check, AlertCircle } from 'lucide-react';

const TermsOfServicePage = ({ onBack }) => {
  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Back Button */}
        <button
          onClick={onBack}
          className="mb-6 text-blue-600 hover:text-blue-800 flex items-center gap-2"
        >
          ← Back to Home
        </button>

        <div className="bg-white rounded-lg shadow-sm p-8">
          <div className="flex items-center gap-3 mb-6">
            <FileText className="w-8 h-8 text-blue-600" />
            <h1 className="text-3xl font-bold text-gray-900">Terms of Service</h1>
          </div>

          <p className="text-gray-700 mb-8">
            Welcome to AbleFam. By using our services, you agree to these terms. 
            Please read them carefully.
          </p>

          {/* Acceptance */}
          <section className="mb-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">1. Acceptance of Terms</h2>
            <p className="text-gray-700 leading-relaxed">
              By accessing or using AbleFam, you agree to be bound by these Terms of Service 
              and our Privacy Policy. If you do not agree to these terms, please do not use 
              our services.
            </p>
          </section>

          {/* Description of Service */}
          <section className="mb-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">2. Description of Service</h2>
            <p className="text-gray-700 leading-relaxed">
              AbleFam provides an online platform for families with children with disabilities 
              to find accessible venues, discover inclusive events, access IEP resources, and 
              connect with other families. Our services are provided for informational and 
              community support purposes only.
            </p>
          </section>

          {/* User Accounts */}
          <section className="mb-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">3. User Accounts</h2>
            <ul className="list-disc list-inside text-gray-700 space-y-2">
              <li>You must provide accurate and complete information when creating an account</li>
              <li>You are responsible for maintaining the security of your account</li>
              <li>You must be at least 18 years old to create an account</li>
              <li>You are responsible for all activities that occur under your account</li>
            </ul>
          </section>

          {/* User Content */}
          <section className="mb-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">4. User-Generated Content</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              You may submit content including venue reviews, community posts, and venue suggestions. 
              By submitting content, you:
            </p>
            <ul className="list-disc list-inside text-gray-700 space-y-2">
              <li>Grant us a license to use, display, and distribute your content</li>
              <li>Represent that you own or have rights to the content</li>
              <li>Agree not to submit content that is false, misleading, or harmful</li>
              <li>Agree not to submit content that violates others' rights</li>
            </ul>
          </section>

          {/* Prohibited Conduct */}
          <section className="mb-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">5. Prohibited Conduct</h2>
            <p className="text-gray-700 leading-relaxed mb-4">You agree not to:</p>
            <ul className="list-disc list-inside text-gray-700 space-y-2">
              <li>Use our services for any illegal purpose</li>
              <li>Harass, abuse, or harm other users</li>
              <li>Post false, misleading, or defamatory content</li>
              <li>Attempt to gain unauthorized access to our systems</li>
              <li>Use automated tools to access or collect data</li>
              <li>Impersonate others or misrepresent your affiliation</li>
            </ul>
          </section>

          {/* Disclaimer */}
          <section className="mb-8">
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-4">
              <div className="flex items-start gap-2">
                <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                <div>
                  <h2 className="text-lg font-semibold text-gray-900 mb-2">6. Disclaimers</h2>
                  <p className="text-gray-700 leading-relaxed">
                    AbleFam is provided "as is" without warranties of any kind. We do not guarantee 
                    the accuracy, completeness, or usefulness of any information on the platform. 
                    Our services are not a substitute for professional medical, legal, or educational advice.
                  </p>
                </div>
              </div>
            </div>
            <p className="text-gray-700 leading-relaxed">
              Please see our full <button onClick={() => {}} className="text-blue-600 hover:underline">Legal Disclaimer</button> for 
              important information about the limitations of our services.
            </p>
          </section>

          {/* Limitation of Liability */}
          <section className="mb-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">7. Limitation of Liability</h2>
            <p className="text-gray-700 leading-relaxed">
              To the maximum extent permitted by law, AbleFam and its operators shall not be 
              liable for any indirect, incidental, special, consequential, or punitive damages 
              arising from your use of our services, including but not limited to damages 
              arising from reliance on information provided through our platform.
            </p>
          </section>

          {/* Indemnification */}
          <section className="mb-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">8. Indemnification</h2>
            <p className="text-gray-700 leading-relaxed">
              You agree to indemnify and hold harmless AbleFam and its operators from any claims, 
              damages, or expenses arising from your use of our services or violation of these terms.
            </p>
          </section>

          {/* Changes to Terms */}
          <section className="mb-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">9. Changes to Terms</h2>
            <p className="text-gray-700 leading-relaxed">
              We may update these terms from time to time. Continued use of our services after 
              changes constitutes acceptance of the new terms.
            </p>
          </section>

          {/* Termination */}
          <section className="mb-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">10. Termination</h2>
            <p className="text-gray-700 leading-relaxed">
              We may suspend or terminate your access to our services at any time for violation 
              of these terms or for any other reason at our discretion.
            </p>
          </section>

          {/* Contact */}
          <section className="mb-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">11. Contact</h2>
            <p className="text-gray-700 leading-relaxed">
              For questions about these Terms of Service, please contact us through our Contact page.
            </p>
          </section>

          {/* Last Updated */}
          <div className="text-sm text-gray-500 pt-6 border-t border-gray-200">
            Last updated: January 2025
          </div>
        </div>
      </div>
    </div>
  );
};

export default TermsOfServicePage;
